import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Check, Search, X, SearchX, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Get a nested value from an object using dot-notation key.
 * e.g., getNestedValue(item, "branch.name") → item.branch.name
 */
function getNestedValue(obj, key) {
  return key.split('.').reduce((acc, part) => acc?.[part], obj);
}

function formatCellValue(val) {
  if (val == null) return '—';
  if (typeof val === 'object') {
    return val.name || val.title || JSON.stringify(val);
  }
  return String(val);
}

export default function TableSelectField({ field, value, onChange, disabled }) {
  const { t } = useTranslation('chat');
  const [search, setSearch] = useState('');

  const items = field.lookup_items || [];
  const columns = field.lookup_columns || [];
  const valueKey = field.lookup_value_key || 'uuid';
  const labelKey = field.lookup_label_key || 'name';

  const filteredItems = useMemo(() => {
    if (!search.trim()) return items;
    const query = search.toLowerCase();
    return items.filter((item) =>
      columns.some((col) => {
        const val = getNestedValue(item, col.key);
        return val != null && formatCellValue(val).toLowerCase().includes(query);
      })
    );
  }, [items, columns, search]);

  const selectedItem = useMemo(
    () => items.find((item) => item[valueKey] === value),
    [items, valueKey, value]
  );

  const handleSelect = (item) => {
    if (disabled) return;
    const itemValue = item[valueKey];
    onChange(value === itemValue ? '' : itemValue);
  };

  const handleClear = () => {
    if (!disabled) onChange('');
  };

  return (
    <div className="space-y-2">
      {selectedItem && (
        <div className="flex items-center gap-2">
          <motion.div
            initial={{ scale: 0.8 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          >
            <Badge variant="default" className="gap-1">
              <Check className="size-3" />
              {selectedItem[labelKey] || selectedItem[valueKey]}
            </Badge>
          </motion.div>
          {!disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}

      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground pointer-events-none" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('search', 'Search...')}
          className="pl-8 pr-8"
          disabled={disabled}
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>

      <ScrollArea className="max-h-72 rounded-lg border border-border">
        <table className="min-w-full divide-y divide-border text-sm">
          <thead className="bg-muted/50 sticky top-0">
            <tr>
              <th className="w-8 px-2 py-2" />
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-2 text-left text-xs font-medium text-muted-foreground"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredItems.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-3 py-8 text-center"
                >
                  <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <SearchX className="size-8 opacity-40" />
                    <span className="text-sm">{t('no_results_table', 'No results found')}</span>
                  </div>
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => {
                const isSelected = item[valueKey] === value;
                return (
                  <tr
                    key={item[valueKey] || item.id}
                    onClick={() => handleSelect(item)}
                    className={cn(
                      'cursor-pointer transition-colors',
                      isSelected
                        ? 'bg-primary/10 hover:bg-primary/15'
                        : index % 2 === 1
                          ? 'bg-muted/20 hover:bg-muted/50'
                          : 'hover:bg-muted/50',
                      disabled && 'pointer-events-none opacity-60'
                    )}
                  >
                    <td className="w-8 px-2 py-2 text-center">
                      {isSelected ? (
                        <Check className="size-4 text-primary mx-auto" />
                      ) : (
                        <Circle className="size-2 text-muted-foreground/40 mx-auto" />
                      )}
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className="px-3 py-2">
                        {formatCellValue(getNestedValue(item, col.key))}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </ScrollArea>
    </div>
  );
}
